import {Validators} from '@angular/forms';

import {MiFidCategoriesEnum, TradingCategoriesEnum} from '@spa/api';

import {
    CompanyNameValidator, EmailValidator,
    InArrayValidator,
    LatinLettersAndSpecialSymbolsAndWhiteSpaceValidator, NameValidator,
    OnlyLatinValidator,
    WhitespaceValidator
} from '@spa/validators';

import * as moment from 'moment';

import {ColorCodesEnum, ComponentTypesEnum} from '../../../../app/shared/enums';
import {
    BaseControl,
    CountryAutocompleteControl,
    DatepickerControl,
    Helper,
    RadioGroupControl
} from '../../../../app/shared/classes';

import {FinancialInstitutionEnum} from '../../../../app/entries/panel/enums';

export const CompanyDetailsNameSelector = 'companyName';
export const CompanyDetailsOrganizationTypeSelector = 'organizationType';
export const CompanyDetailsDateOfIncorporationSelector = 'dateOfIncorporation';
export const CompanyDetailsCountryOfIncorporationSelector = 'countryOfIncorporation';
export const CompanyMiFIDCategorySelector = 'MiFidCategory';
export const CompanyDetailsFinancialInstitutionSelector = 'financialInstitution';
export const CompanyDetailsTradingCategorySelector = 'tradingCategory';
export const CompanyEmployeeDetailsFirstNameSelector = 'firstName';
export const CompanyEmployeeDetailsMiddleNameSelector = 'middleName';
export const CompanyEmployeeDetailsLastNameSelector = 'lastName';
export const CompanyReferralCodeSelector = 'referral';
export const CompanyOldClientIdSelector = 'oldClientId';
export const CompanySecondaryEmailSelector = 'secondaryEmail';
export const CompanyReferralCodeMaxLength = 100;
export const CompanyOldClientIdMaxLengthValidation = 128;

export const CompanyDetailsControls = (): BaseControl[] => {
    return [
        new BaseControl({
            name: CompanyDetailsNameSelector,
            value: '',
            placeholder: 'Company Name',
            validators: [WhitespaceValidator(), CompanyNameValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        }),
        new BaseControl({
            name: CompanyDetailsOrganizationTypeSelector,
            value: '',
            placeholder: 'Organization Type',
            validators: [WhitespaceValidator(), OnlyLatinValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        }),
        new DatepickerControl({
            name: CompanyDetailsDateOfIncorporationSelector,
            value: '',
            placeholder: 'Date Of Incorporation',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Datepicker,
            maxDate: moment().format('YYYY-MM-DD'),
            minDate: moment('1930-01-01').format('YYYY-MM-DD'),
            floatingLabelNever: true
        }),
        new CountryAutocompleteControl({
            name: CompanyDetailsCountryOfIncorporationSelector,
            value: null,
            placeholder: 'Country Of Incorporation',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.CountryAutocomplete,
            options: [],
            countries: [],
            floatingLabelNever: true
        }),
        new RadioGroupControl({
            name: CompanyMiFIDCategorySelector,
            value: null,
            placeholder: 'MiFID Category',
            validators: [
                Validators.required,
                InArrayValidator([
                    MiFidCategoriesEnum.Retail,
                    MiFidCategoriesEnum.Professional,
                    MiFidCategoriesEnum.Eligible
                ])
            ],
            componentType: ComponentTypesEnum.RadioGroup,
            options: [
                {
                    label: Helper.textsHelper().getMiFidCategoryName(MiFidCategoriesEnum.Retail),
                    value: MiFidCategoriesEnum.Retail,
                    color: ColorCodesEnum.Primary
                },
                {
                    label: Helper.textsHelper().getMiFidCategoryName(MiFidCategoriesEnum.Professional),
                    value: MiFidCategoriesEnum.Professional,
                    color: ColorCodesEnum.Primary
                },
                {
                    label: Helper.textsHelper().getMiFidCategoryName(MiFidCategoriesEnum.Eligible),
                    value: MiFidCategoriesEnum.Eligible,
                    color: ColorCodesEnum.Primary
                }
            ],
            floatingLabelNever: true
        }),
        new RadioGroupControl({
            name: CompanyDetailsFinancialInstitutionSelector,
            value: FinancialInstitutionEnum.No,
            placeholder: 'Financial Institution',
            validators: [
                Validators.required,
                InArrayValidator([FinancialInstitutionEnum.No, FinancialInstitutionEnum.Yes])
            ],
            componentType: ComponentTypesEnum.RadioGroup,
            options: [
                {
                    label: Helper.textsHelper().getFinancialInstitutionOptionName(FinancialInstitutionEnum.No),
                    value: FinancialInstitutionEnum.No,
                    color: ColorCodesEnum.Primary,
                    checked: true
                },
                {
                    label: Helper.textsHelper().getFinancialInstitutionOptionName(FinancialInstitutionEnum.Yes),
                    value: FinancialInstitutionEnum.Yes,
                    color: ColorCodesEnum.Primary
                }
            ],
            floatingLabelNever: true
        }),
        new RadioGroupControl({
            name: CompanyDetailsTradingCategorySelector,
            value: null,
            placeholder: 'Investment Firm',
            validators: [
                Validators.required,
                InArrayValidator([TradingCategoriesEnum.Retail, TradingCategoriesEnum.Group])
            ],
            componentType: ComponentTypesEnum.RadioGroup,
            options: [
                {
                    label: Helper.textsHelper().getIsInvestmentFirm(TradingCategoriesEnum.Retail),
                    value: TradingCategoriesEnum.Retail,
                    color: ColorCodesEnum.Primary,
                },
                {
                    label: Helper.textsHelper().getIsInvestmentFirm(TradingCategoriesEnum.Group),
                    value: TradingCategoriesEnum.Group,
                    color: ColorCodesEnum.Primary
                }
            ],
            floatingLabelNever: true
        }),
        new BaseControl({
            name: CompanyReferralCodeSelector,
            value: '',
            placeholder: 'Referral Code',
            validators: [Validators.maxLength(CompanyReferralCodeMaxLength),
            LatinLettersAndSpecialSymbolsAndWhiteSpaceValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        }),
        new BaseControl({
            name: CompanyOldClientIdSelector,
            value: '',
            placeholder: 'Old Client Id',
            validators: [Validators.maxLength(CompanyOldClientIdMaxLengthValidation)],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        }),
        new BaseControl({
            name: CompanySecondaryEmailSelector,
            value: '',
            placeholder: 'Secondary Email',
            validators: [EmailValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        })
    ];
};

export const CompanyEmployeeControls = (): BaseControl[] => {
    return [
        new BaseControl({
            name: CompanyEmployeeDetailsFirstNameSelector,
            value: '',
            placeholder: 'First Name',
            validators: [WhitespaceValidator(), NameValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        }),
        new BaseControl({
            name: CompanyEmployeeDetailsMiddleNameSelector,
            value: '',
            placeholder: 'Middle Name',
            validators: [NameValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        }),
        new BaseControl({
            name: CompanyEmployeeDetailsLastNameSelector,
            value: '',
            placeholder: 'Last Name',
            validators: [WhitespaceValidator(), NameValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        })
    ];
};
